import React, { useState } from "react";
import "./create-card.scss";

/** Tiny inline SVGs so we don't depend on external /src icons */
const Eye = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden focusable="false" {...props}>
    <path fill="currentColor" d="M12 5c5 0 9 4.5 10 7-1 2.5-5 7-10 7S3 14.5 2 12c1-2.5 5-7 10-7zm0 3.5A3.5 3.5 0 1 0 12 15a3.5 3.5 0 0 0 0-7z"/>
  </svg>
);

const Chip = ({ children, onRemove }) => (
  <span className="chip">
    {children}
    {!!onRemove && (
      <button type="button" className="chip__x" aria-label={`Remove ${children}`} onClick={onRemove}>
        ×
      </button>
    )}
  </span>
);

const DropZone = ({ label, id }) => (
  <label className="dz">
    <input id={id} type="file" className="dz__input" />
    <div className="dz__icon" aria-hidden>🖼️</div>
    <div className="dz__text">
      <strong>{label}</strong>
      <span>Drag & drop or click to upload</span>
    </div>
  </label>
);

const ElementCreateCards = () => {
  const [tags, setTags] = useState(["PC", "On eBay"]);
  const [variants, setVariants] = useState(["Rookie (RC)", "Patch"]);

  return (
    <main className="create-page">
      {/* page-level décor lives outside content */}
      <span className="page-glow page-glow--bottom" aria-hidden />
      <span className="page-glow page-glow--left" aria-hidden />

      <section className="container">
        <header className="create__header">
          <h1>Create Card</h1>
          <p>Add the card info, images, and pricing. You can edit everything later.</p>
        </header>

        <form
          className="create__grid"
          onSubmit={(e) => {
            e.preventDefault();
            // TODO hook to real submit
          }}
        >
          {/* COLUMN 1 — Basic Card Info */}
          <fieldset className="panel">
            <legend>Basic Card Info</legend>

            <div className="fields">
              <label className="field">
                <span>QuickSearch *</span>
                <input type="text" placeholder="Search for a DeepSearch" required />
              </label>

              <label className="field">
                <span>Player *</span>
                <input type="text" placeholder="Search for a Player" required />
              </label>

              <label className="field">
                <span>Uniform *(impacts team filter)</span>
                <input type="text" placeholder="e.g. New York Rangers" />
              </label>

              <label className="field">
                <span>Set *</span>
                <input type="text" placeholder="e.g. 2018-19 Upper Deck" required />
              </label>

              <label className="field">
                <span>Card name (optional)</span>
                <input type="text" placeholder="Select the card name" />
              </label>

              <label className="field">
                <span>Card name (optional)</span>
                <input type="text" placeholder="Select the card name" />
              </label>
            </div>
          </fieldset>

          {/* COLUMN 2 — Attributes & Grade */}
          <div className="column">
            <fieldset className="panel">
              <legend>Attributes</legend>

              <label className="field">
                <span>Tags</span>
                <div className="chips">
                  {tags.map((t) => (
                    <Chip key={t} onRemove={() => setTags(tags.filter((x) => x !== t))}>{t}</Chip>
                  ))}
                  <input
                    className="chip-input"
                    type="text"
                    placeholder="Add tag"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.currentTarget.value.trim()) {
                        e.preventDefault();
                        setTags((s) => Array.from(new Set([...s, e.currentTarget.value.trim()])));
                        e.currentTarget.value = "";
                      }
                    }}
                  />
                </div>
              </label>
            </fieldset>

            <fieldset className="panel">
              <legend>Card Grade</legend>

              <label className="field">
                <span>Grading Status</span>
                <select defaultValue="raw">
                  <option value="raw">Raw</option>
                  <option value="graded">Graded</option>
                </select>
              </label>

              <label className="field">
                <span>Condition</span>
                <select defaultValue="nm">
                  <option value="mint">Mint</option>
                  <option value="nm">Near Mint (NM)</option>
                  <option value="ex">Excellent (EX)</option>
                  <option value="vg">Very Good (VG)</option>
                </select>
              </label>
            </fieldset>
          </div>

          {/* COLUMN 3 — Price + Images */}
          <div className="column">
            <fieldset className="panel">
              <legend className="panel__row">
                <span>Price</span>
                <button type="button" className="btn-ghost">
                  <Eye /> <span>See comps on eBay</span>
                </button>
              </legend>

              <div className="fields">
                <label className="field">
                  <span>Estimate Value ($)</span>
                  <input type="number" inputMode="decimal" placeholder="0" min="0" />
                </label>

                <label className="field">
                  <span>Price Paid ($)</span>
                  <input type="number" inputMode="decimal" placeholder="0" min="0" />
                </label>

                <label className="field">
                  <span>Location</span>
                  <input type="text" placeholder="Location (C21)" />
                </label>

                <label className="field">
                  <span>Pre-Assign to Collection</span>
                  <select defaultValue="">
                    <option value="" disabled>
                      Select…
                    </option>
                    <option>Example Collection</option>
                  </select>
                </label>
              </div>
            </fieldset>

            <fieldset className="panel">
              <legend>Card Images</legend>
              <div className="dz-grid">
                <DropZone id="img-front" label="Front of card" />
                <DropZone id="img-back" label="Back of card" />
              </div>
            </fieldset>
          </div>

          {/* FULL-WIDTH — Variants & details */}
          <fieldset className="panel panel--full">
            <legend>Details</legend>

            <div className="fields fields--3">
              <label className="field">
                <span>Variants</span>
                <div className="chips">
                  {variants.map((v) => (
                    <Chip key={v} onRemove={() => setVariants(variants.filter((x) => x !== v))}>
                      {v}
                    </Chip>
                  ))}
                  <input
                    className="chip-input"
                    type="text"
                    placeholder="Add variant"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.currentTarget.value.trim()) {
                        e.preventDefault();
                        setVariants((s) => Array.from(new Set([...s, e.currentTarget.value.trim()])));
                        e.currentTarget.value = "";
                      }
                    }}
                  />
                </div>
              </label>

              <label className="field">
                <span>Variation (optional)</span>
                <input type="text" placeholder="Select the variation" />
              </label>

              <label className="field">
                <span>Is this card numbered?</span>
                <input type="text" placeholder="e.g. 15/99" />
              </label>

              <label className="field">
                <span>Special Edition Code</span>
                <input type="text" placeholder="e.g. SSP-HS-4clr" />
              </label>

              <label className="field">
                <span>Back of Card Number</span>
                <input type="text" placeholder="e.g. #27" />
              </label>
            </div>
          </fieldset>

          <div className="create__actions">
            <button type="button" className="btn-ghost">Discard</button>
            <button type="submit" className="btn-primary">Add Card</button>
          </div>
        </form>
      </section>
    </main>
  );
};

export default ElementCreateCards;
